/**
 * Chat: channels per team and company, direct and group messages, and the threads of tasks I'm on. Two panes on a
 * desktop (three with a reply thread open); on a phone the list, one conversation and a thread are separate screens.
 * Messages arrive live (lib/chat.ts stream). The pieces are in components/chat/Thread.tsx.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import clsx from "clsx";
import { ArrowLeft, Bell, BellOff, Hash, ListChecks, Pin, Plus, Search, Users, Volume2, VolumeX, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api, post } from "../lib/api";
import { conversationTitle, setActiveConversation, setPresence, setSoundOn, useConversations, usePresence, usePresenceList, useSoundOn, type Conversation, type Message } from "../lib/chat";
import { Avatar, Empty, ErrorNote, Modal } from "../components/ui";
import { PersonPicker } from "../components/work";
import { Composer, fmtWhen, MessageItem, MessageList, ReplyPanel, TaskCard } from "../components/chat/Thread";
import type { Person } from "../lib/work";

export function ChatPage() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const convs = useConversations();
  const [starting, setStarting] = useState(false);
  const [q, setQ] = useState("");
  const items = convs.data?.items ?? [];
  const me = convs.data?.me ?? "";
  const pres = usePresenceList();
  useEffect(() => { if (pres.data) setPresence(pres.data.items); }, [pres.data]);
  const channels = items.filter((c) => c.kind === "channel");
  const direct = items.filter((c) => c.kind === "direct" || c.kind === "group");
  // task threads: only ones with a message; "Hide done" (remembered on this device) drops done/cancelled tasks.
  // The open thread always stays listed. (Liam, 2026-11-04)
  const [hideDone, setHideDone] = useState(() => { try { return localStorage.getItem(HIDE_DONE_KEY) !== "0"; } catch { return true; } });
  const toggleHideDone = (v: boolean) => { setHideDone(v); try { localStorage.setItem(HIDE_DONE_KEY, v ? "1" : "0"); } catch { /* private mode */ } };
  const tasks = items.filter((c) => c.kind === "task" && (c.id === id || (!!c.last && !(hideDone && c.item && (c.item.state === "done" || c.item.state === "cancelled")))));
  const anyTaskThreads = items.some((c) => c.kind === "task" && c.last);
  return (
    <div className="-mx-4 -mt-5 -mb-28 flex h-[calc(100dvh-4rem)] md:-mx-6 md:-mt-7 md:-mb-10 xl:-mx-8">
      <aside className={clsx("w-full shrink-0 flex-col border-r border-[var(--line)] bg-[var(--panel)] md:flex md:w-72", id ? "hidden" : "flex")}>
        <div className="flex items-center gap-2 px-4 pt-4 pb-2">
          <h1 className="text-lg font-semibold flex-1">Chat</h1>
          <SoundSwitch />
          <button className="btn btn-sm" onClick={() => setStarting(true)} aria-label="New message" title="New message"><Plus size={14} /> New</button>
        </div>
        <div className="relative px-3 pb-2">
          <Search size={14} className="subtle absolute left-6 top-[11px]" />
          <input className="input !min-h-0 !py-2 !pl-8 text-sm" placeholder="Search messages" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search messages" />
          {q && <button className="absolute right-5 top-[9px]" aria-label="Clear search" onClick={() => setQ("")}><X size={14} className="muted" /></button>}
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-24 md:pb-2">
          {q.trim().length >= 2 ? <SearchResults q={q.trim()} /> : (
            <>
              <ErrorNote error={convs.error} />
              {convs.isLoading && <p className="muted px-2 py-3 text-sm">Loading…</p>}
              {channels.length > 0 && <p className="section-label px-2 pt-2">Channels</p>}
              {channels.map((c) => <ConvRow key={c.id} c={c} me={me} active={c.id === id} />)}
              <p className="section-label px-2 pt-3">Direct</p>
              {direct.length === 0 && !convs.isLoading && <p className="muted px-2 py-2 text-xs">No direct messages yet. Press New to start one.</p>}
              {direct.map((c) => <ConvRow key={c.id} c={c} me={me} active={c.id === id} />)}
              {anyTaskThreads && (
                <div className="flex items-center justify-between gap-2 px-2 pt-3">
                  <p className="section-label">Task threads</p>
                  <label className="muted flex cursor-pointer items-center gap-1.5 text-[11px]">
                    Hide done
                    <button type="button" role="switch" aria-checked={hideDone} aria-label="Hide done tasks" onClick={() => toggleHideDone(!hideDone)}
                      className={clsx("relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition", hideDone ? "bg-brand-600" : "bg-[var(--line-strong)]")}>
                      <span className={clsx("inline-block h-3 w-3 rounded-full bg-white shadow transition", hideDone ? "translate-x-[14px]" : "translate-x-0.5")} />
                    </button>
                  </label>
                </div>
              )}
              {anyTaskThreads && tasks.length === 0 && <p className="muted px-2 py-2 text-xs">No open task threads.</p>}
              {tasks.map((c) => <ConvRow key={c.id} c={c} me={me} active={c.id === id} />)}
            </>
          )}
        </div>
      </aside>
      <section className={clsx("min-w-0 flex-1 flex-col md:flex", id ? "flex" : "hidden")}>
        {id ? <ConversationPane key={id} id={id} /> : <Empty icon={<Hash size={20} />}>Pick a channel, a person or a task thread on the left.</Empty>}
      </section>
      {starting && <NewMessage onClose={() => setStarting(false)} />}
    </div>
  );
}

const HIDE_DONE_KEY = "one.chat.hideDoneTasks";

function SoundSwitch() {
  const on = useSoundOn();
  return <button className="btn btn-sm" title={on ? "Sound on for new messages" : "Sound off"} aria-label={on ? "Turn sound off" : "Turn sound on"} aria-pressed={on} onClick={() => setSoundOn(!on)}>{on ? <Volume2 size={14} /> : <VolumeX size={14} />}</button>;
}

function ConvRow({ c, me, active }: { c: Conversation; me: string; active: boolean }) {
  const title = conversationTitle(c, me);
  // task threads: the task's full name, wrapped, without the WRK number (it stays in the thread header) — Liam, 2026-11-04
  const isTask = c.kind === "task" && !!c.item;
  const online = usePresence();
  const other = c.kind === "direct" ? (c.members ?? []).find((m) => m.id !== me) : null;
  return (
    <Link to="/chat/$id" params={{ id: c.id }} className={clsx("flex items-center gap-2.5 rounded-xl px-2 py-2 transition-colors hover:bg-[#f4f5fb]", active && "bg-brand-50")}>
      {c.kind === "channel" ? <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#eceef5] text-[var(--muted)]"><Hash size={15} /></span>
        : c.kind === "group" ? <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#eceef5] text-[var(--muted)]"><Users size={15} /></span>
        : c.kind === "task" ? <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-600"><ListChecks size={15} /></span>
        : <span className="relative shrink-0"><Avatar name={title} size={32} />{other && online[other.id] && <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[var(--panel)] bg-emerald-500" title="Online" />}</span>}
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className={clsx(isTask ? "min-w-0 break-words text-sm leading-snug" : "truncate text-sm", c.unread ? "font-semibold" : "font-medium", c.kind === "task" && c.item && (c.item.state === "done" || c.item.state === "cancelled") && "text-[var(--muted)] line-through")}>{isTask ? c.item!.title : title}</span>
          {c.last && <span className="muted ml-auto shrink-0 text-[11px]">{fmtWhen(c.last.at)}</span>}
        </span>
        <span className="flex items-center gap-2">
          <span className={clsx("muted truncate text-xs", c.unread && "text-[var(--text)]")}>{c.last ? `${c.kind !== "direct" ? `${c.last.authorName.split(" ")[0]}: ` : ""}${c.last.body}` : c.kind === "channel" ? `${c.memberCount} members` : "No messages yet"}</span>
          {c.muted && <BellOff size={11} className="muted shrink-0" />}
          {c.unread > 0 && <span className="ml-auto shrink-0 rounded-full bg-brand-600 px-1.5 text-[10px] font-bold text-white num">{c.unread > 99 ? "99+" : c.unread}</span>}
        </span>
      </span>
    </Link>
  );
}

function SearchResults({ q }: { q: string }) {
  const r = useQuery({ queryKey: ["chat", "search", q], queryFn: () => api<{ items: Array<{ id: string; conversationId: string; parentId: string | null; kind: Conversation["kind"]; conversationName: string | null; createdAt: string; authorName: string; body: string; item: { ref: string; title: string } | null; others: string | null }> }>(`/api/chat/search?q=${encodeURIComponent(q)}`) });
  const items = r.data?.items ?? [];
  return (
    <div>
      <ErrorNote error={r.error} />
      {r.isLoading && <p className="muted px-2 py-3 text-sm">Searching…</p>}
      {r.data && items.length === 0 && <p className="muted px-2 py-3 text-sm">Nothing matches “{q}”.</p>}
      {items.map((m) => (
        <a key={m.id} href={`/chat/${m.conversationId}${m.parentId ? `?thread=${m.parentId}` : ""}`} className="block rounded-xl px-2 py-2 hover:bg-[#f4f5fb]">
          <span className="flex items-baseline gap-2 text-xs"><span className="truncate font-semibold text-brand-700">{m.kind === "task" && m.item ? `${m.item.ref} ${m.item.title}` : m.kind === "channel" ? `#${m.conversationName}` : m.conversationName ?? m.others ?? "Chat"}</span><span className="muted ml-auto shrink-0">{fmtWhen(m.createdAt)}</span></span>
          <span className="block truncate text-sm"><span className="font-medium">{m.authorName.split(" ")[0]}:</span> {m.body}</span>
        </a>
      ))}
    </div>
  );
}

function NewMessage({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [people, setPeople] = useState<Person[]>([]);
  const [name, setName] = useState("");
  const m = useMutation({
    mutationFn: () => post<{ id: string }>("/api/chat/conversations", { personIds: people.map((p) => p.id), ...(people.length > 1 && name.trim() ? { name: name.trim() } : {}) }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["chat", "conversations"] }); onClose(); void nav({ to: "/chat/$id", params: { id: r.id } }); },
  });
  return (
    <Modal title="New message" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!people.length || m.isPending} onClick={() => m.mutate()}>Start</button></>}>
      <ErrorNote error={m.error} />
      <PersonPicker value={people} onChange={setPeople} placeholder="Who do you want to message?" />
      {people.length > 1 && <input className="input mt-3" placeholder="Group name (optional)" value={name} onChange={(e) => setName(e.target.value)} />}
      <p className="muted mt-2 text-xs">One person opens your direct chat with them. Two or more makes a group.</p>
    </Modal>
  );
}

interface Head { id: string; kind: Conversation["kind"]; name: string | null; itemId: string | null; item: { id: string; ref: string; title: string; state: string; dueAt: string | null; ownerName: string | null; statusLabel: string | null } | null; members: Array<{ id: string; name: string; lastSeenAt: string | null; online: boolean }>; me: string; pinnedCount: number }

function ConversationPane({ id }: { id: string }) {
  const qc = useQueryClient();
  const head = useQuery({ queryKey: ["chat", "conversation", id], queryFn: () => api<Head>(`/api/chat/conversations/${id}`) });
  const convs = useConversations();
  const conv = convs.data?.items.find((c) => c.id === id);
  const emoji = convs.data?.emoji ?? [];
  const [thread, setThread] = useState<string | null>(() => new URLSearchParams(location.search).get("thread"));
  const [showPeople, setShowPeople] = useState(false);
  const [showPinned, setShowPinned] = useState(false);
  const msgs = useQuery({ queryKey: ["chat", "messages", id], queryFn: () => api<{ items: Message[]; more: boolean }>(`/api/chat/conversations/${id}/messages`), enabled: false });
  const lastId = msgs.data?.items[msgs.data.items.length - 1]?.id;

  useEffect(() => { setActiveConversation(id); return () => setActiveConversation(null); }, [id]);
  useEffect(() => {
    if (document.visibilityState !== "visible") return;
    void post(`/api/chat/conversations/${id}/read`, {}).then(() => { void qc.invalidateQueries({ queryKey: ["chat", "conversations"] }); void qc.invalidateQueries({ queryKey: ["chat", "unread"] }); void qc.invalidateQueries({ queryKey: ["inbox"] }); });
  }, [id, lastId, qc]);

  const mute = useMutation({ mutationFn: (muted: boolean) => post(`/api/chat/conversations/${id}/mute`, { muted }), onSuccess: () => void qc.invalidateQueries({ queryKey: ["chat", "conversations"] }) });
  const h = head.data;
  const online = usePresence();
  useEffect(() => { if (h) setPresence(h.members.map((m) => ({ id: m.id, online: m.online }))); }, [h]);
  const other = h?.kind === "direct" ? h.members.find((m) => m.id !== h.me) : null;
  const lastSeen = (iso: string | null) => { if (!iso) return "Not seen yet"; const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000); return mins < 2 ? "Last seen just now" : mins < 60 ? `Last seen ${mins} min ago` : mins < 60 * 24 ? `Last seen ${Math.round(mins / 60)} h ago` : `Last seen ${new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`; };
  const onlineCount = h ? h.members.filter((m) => m.id !== h.me && online[m.id]).length : 0;
  const title = h ? (h.kind === "channel" ? h.name ?? "Channel" : h.kind === "task" && h.item ? h.item.title : (h.name ?? (h.members.filter((m) => m.id !== h.me).map((m) => m.name).join(", ") || "Just you"))) : "";

  return (
    <div className="flex h-full min-w-0">
      <div className={clsx("flex min-w-0 flex-1 flex-col", thread && "hidden lg:flex")}>
        <header className="flex items-center gap-2 border-b border-[var(--line)] bg-[var(--panel)] px-3 py-2.5 md:px-4">
          <Link to="/chat" className="btn btn-sm md:hidden" aria-label="Back to chats"><ArrowLeft size={16} /></Link>
          {h?.kind === "channel" ? <Hash size={18} className="muted" /> : h?.kind === "group" ? <Users size={18} className="muted" /> : h?.kind === "task" ? <ListChecks size={18} className="text-brand-600" /> : <Avatar name={title} size={28} />}
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[0.9375rem] font-semibold">{title}</h2>
            {h && (
              <button className="muted text-xs hover:underline" onClick={() => setShowPeople((x) => !x)}>
                {other ? (online[other.id] ? <span className="inline-flex items-center gap-1 text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" />Online</span> : lastSeen(other.lastSeenAt)) : <>{h.members.length} {h.members.length === 1 ? "member" : "members"}{onlineCount > 0 ? ` · ${onlineCount} online` : ""}</>}
              </button>
            )}
          </div>
          {h && h.pinnedCount > 0 && <button className={clsx("btn btn-sm", showPinned && "!border-brand-400 !bg-brand-50")} onClick={() => setShowPinned((x) => !x)} title="Pinned messages" aria-pressed={showPinned}><Pin size={14} /> {h.pinnedCount}</button>}
          {conv && <button className="btn btn-sm" title={conv.muted ? "Unmute" : "Mute"} aria-label={conv.muted ? "Unmute" : "Mute"} onClick={() => mute.mutate(!conv.muted)}>{conv.muted ? <BellOff size={15} /> : <Bell size={15} />}</button>}
        </header>
        {h?.kind === "task" && h.item && (
          <div className="border-b border-[var(--line)] bg-[var(--panel)] px-3 py-2 md:px-4">
            <TaskCard t={{ ...h.item, assigneeNames: null, threadCount: 0, threadLastAt: null }} />
            <p className="muted mt-1 text-[11px]">This is the task's own thread. Everything written here shows on the task page too; its changes are shown there.</p>
          </div>
        )}
        {showPeople && h && (
          <div className="flex flex-wrap gap-1.5 border-b border-[var(--line)] bg-[var(--panel)] px-4 py-2 text-xs">
            {h.members.map((m) => <span key={m.id} className="chip inline-flex items-center gap-1">{online[m.id] && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}{m.name}</span>)}
            {h.kind === "channel" && <span className="muted self-center">· follows the team in the directory</span>}
            {h.kind === "task" && <span className="muted self-center">· the people on the task</span>}
          </div>
        )}
        {showPinned && <PinnedList conversationId={id} emoji={emoji} onClose={() => setShowPinned(false)} />}
        <div className="flex-1 overflow-y-auto px-3 py-3 md:px-5">
          <MessageList conversationId={id} emoji={emoji} onReply={(m) => setThread(m.id)} />
        </div>
        <div className="border-t border-[var(--line)] bg-[var(--panel)] px-3 pb-20 pr-20 pt-2 md:px-4 md:pb-3 md:pr-4">
          <Composer conversationId={id} placeholder={title ? `Message ${h?.kind === "channel" ? "#" : ""}${title}` : "Message"} />
          <p className="muted mt-1 hidden text-[11px] md:block">Enter sends · Shift+Enter for a new line · @ to mention · hover a message to react, reply or make a task</p>
        </div>
      </div>
      {thread && (
        <aside className="flex w-full min-w-0 flex-col border-l border-[var(--line)] bg-[var(--panel)] lg:w-96 lg:shrink-0">
          <ReplyPanel conversationId={id} parentId={thread} emoji={emoji} onClose={() => setThread(null)} />
        </aside>
      )}
    </div>
  );
}

function PinnedList({ conversationId, emoji, onClose }: { conversationId: string; emoji: string[]; onClose: () => void }) {
  const q = useQuery({ queryKey: ["chat", "messages", conversationId, "pinned"], queryFn: () => api<{ items: Message[] }>(`/api/chat/conversations/${conversationId}/messages?pinned=1`) });
  return (
    <div className="max-h-64 overflow-y-auto border-b border-[var(--line)] bg-amber-50/60 px-3 py-2">
      <div className="flex items-center gap-2 text-xs font-semibold"><Pin size={12} /> Pinned<button className="ml-auto" aria-label="Hide pinned" onClick={onClose}><X size={14} /></button></div>
      {q.data?.items.map((m) => <MessageItem key={m.id} m={m} emoji={emoji} compact inThread />)}
      {q.data && q.data.items.length === 0 && <p className="muted text-xs">Nothing pinned.</p>}
    </div>
  );
}
